import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Mail } from "lucide-react";
import { useEffect, useState } from "react";

import { SiteFooter } from "@/components/site-footer";
import { SiteHeader } from "@/components/site-header";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { claimInvite } from "@/lib/sharing.functions";

// Read by the _authenticated layout right after sign-in, so a fresh signup
// (not just an existing session) can still land back here — the invite
// route itself is public and does the actual claiming, but someone who
// wasn't signed in yet needs somewhere to come back to.
export const PENDING_INVITE_TOKEN_KEY = "understandyourgenome:pending-invite-token";

export const Route = createFileRoute("/invite/$token")({
  head: () => ({
    meta: [{ title: "Shared report — Understand Your Genome" }],
  }),
  component: InvitePage,
});

function InvitePage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const claim = useServerFn(claimInvite);
  const [state, setState] = useState<"checking" | "needs-auth" | "claiming" | "error">("checking");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const { data } = await supabase.auth.getSession();
      if (cancelled) return;

      if (!data.session) {
        localStorage.setItem(PENDING_INVITE_TOKEN_KEY, token);
        setState("needs-auth");
        return;
      }

      setState("claiming");
      try {
        const result = await claim({ data: { token } });
        if (cancelled) return;
        localStorage.removeItem(PENDING_INVITE_TOKEN_KEY);
        navigate({ to: "/report/$jobId", params: { jobId: result.jobId } });
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "This invite link isn't valid.");
        setState("error");
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [token, claim, navigate]);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="mx-auto w-full max-w-md flex-1 px-5 py-20 text-center">
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Mail className="size-6" />
        </div>

        {(state === "checking" || state === "claiming") && (
          <>
            <h1 className="mt-5 text-2xl">Opening your shared report…</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">One moment.</p>
          </>
        )}

        {state === "needs-auth" && (
          <>
            <h1 className="mt-5 text-2xl">You've been invited to view a genome report</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Sign in or create a free account to open it — we'll bring you right back here.
            </p>
            <Button asChild className="mt-6">
              <Link to="/auth">Sign in or create an account</Link>
            </Button>
          </>
        )}

        {state === "error" && (
          <>
            <h1 className="mt-5 text-2xl">We couldn't open that</h1>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{error}</p>
            <Button asChild className="mt-6" variant="outline">
              <Link to="/dashboard">Go to my dashboard</Link>
            </Button>
          </>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
